'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import instance from '@/lib/axiosInstance';
import echo from '@/lib/echo';
import Echo from 'laravel-echo';
import { useAuth } from '@/context/AuthContext';

export type Notification = {
  id: string;
  type: string;
  notifiable_type: string;
  notifiable_id: number;
  data: {
    type?: string;
    message: string;
    title: string;
    notification_title: string;
    message_key: string;
    message_params: Record<string, any>;
    follow_request_id?: number;
    action_url?: string;
  };
  read_at: string | null;
  created_at: string;
  updated_at: string;
};

type NotificationsContextType = {
  isOpen: boolean;
  notifications: Notification[];
  unreadCount: number;
  openNotifications: () => void;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  closeNotifications: () => void;
  markAllAsRead: () => void;
  markAsRead: (id: string) => void;
  // Add these methods for toast notifications
  onNewNotification: (callback: (notification: Notification) => void) => void;
  offNewNotification: (callback: (notification: Notification) => void) => void;
};

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newNotificationCallbacks, setNewNotificationCallbacks] = useState<Set<(notification: Notification) => void>>(new Set());
  const { user, isLoading } = useAuth();
  const userId = user?.id;

  const unreadCount = notifications.filter(n => !n.read_at).length;

  // Event emitter functions
  const onNewNotification = useCallback((callback: (notification: Notification) => void) => {
    setNewNotificationCallbacks(prev => new Set([...prev, callback]));
  }, []);

  const offNewNotification = useCallback((callback: (notification: Notification) => void) => {
    setNewNotificationCallbacks(prev => {
      const newSet = new Set(prev);
      newSet.delete(callback);
      return newSet;
    });
  }, []);

  // Function to emit new notification event
  const emitNewNotification = useCallback((notification: Notification) => {
    newNotificationCallbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification callback:', error);
      }
    });
  }, [newNotificationCallbacks]);

  useEffect(() => {
    // Don't proceed if auth is still loading
    if (isLoading) {
      return;
    }

    if (!userId) {
      return; // Return here since we need userId for channel name
    }

    // Skip echo setup in development/local environment
    const isLocal = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_ENV === 'development';
    
    if (isLocal) {
      console.log('🔧 Running in local/development mode - skipping Echo setup');
      fetchNotifications();
      return;
    }

    let echoInstance: Echo<any> | null = null;
    let channelInstance: any = null;
    const channelName = `users.${userId}`;

    // Initialize echo and set up listeners
    const setupEcho = async () => {
      try {
        echoInstance = echo;

        if (!echoInstance) {
          console.error('❌ Echo is not initialized.');
          fetchNotifications();
          return;
        }


        // Subscribe to private channel for notifications
        channelInstance = echoInstance.private(channelName);

        // Set up error handler
        channelInstance.error((error: any) => {
          console.error('❌ Error subscribing to channel:', error);
        });

        // Listen for Laravel notifications using .notification() method
        channelInstance.notification((notification: any) => {
          
          if (!notification || !notification.id) {
            console.warn('⚠️ Received malformed notification data:', notification);
            return;
          }

          // Laravel notifications come in a specific format
          const processedNotification: Notification = {
            id: notification.id,
            type: notification.type,
            notifiable_type: 'App\\Models\\User',
            notifiable_id: userId as number,
            data: notification.data || {},
            read_at: notification.read_at || null,
            created_at: notification.created_at || new Date().toISOString(),
            updated_at: notification.updated_at || new Date().toISOString()
          };


          // Add to notifications list (check for duplicates)
          setNotifications(prev => {
            // Check if notification already exists
            const exists = prev.some(existing => existing.id === processedNotification.id);
            if (exists) {
              console.log('🔄 Notification already exists, skipping duplicate:', processedNotification.id);
              return prev;
            }
            return [processedNotification, ...prev];
          });
          
          // Emit the new notification event for toast
          emitNewNotification(processedNotification);
        });

        // Fetch existing notifications only initially, not after setting up listeners
        await fetchNotifications();
      } catch (error) {
        console.error('❌ Failed to set up channel subscription:', error);
        fetchNotifications();
      }
    };

    setupEcho();

    // Cleanup function
    return () => {
      if (channelInstance && echoInstance) {
        try {
          // Leave the private channel
          echoInstance.leave(`private-${channelName}`);
        } catch (error) {
          console.error('❌ Error during cleanup:', error);
        }
      }
    };
  }, [userId, isLoading, emitNewNotification]);

  const fetchNotifications = async () => {
    if (!userId) {
      return;
    }

    try {
      const response = await instance.get('/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('❌ Failed to fetch notifications', error);
    }
  };

  const openNotifications = () => setIsOpen(true);
  const closeNotifications = () => setIsOpen(false);

  const markAsRead = async (id: string) => {
    try {
      await instance.post(`/notifications/${id}/mark-as-read`);
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        )
      );
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await instance.post('/notifications/mark-all-as-read');
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read_at: new Date().toISOString() }))
      );
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        isOpen,
        notifications,
        setNotifications,
        unreadCount,
        openNotifications,
        closeNotifications,
        markAsRead,
        markAllAsRead,
        onNewNotification,
        offNewNotification
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};